import {
  SITE_BETA_DISCLAIMER_DISMISSED_AT_KEY,
  SITE_BETA_TOOLBAR_ELIGIBLE_KEY,
} from './constants/siteDisclaimer';

export const BETA_ENGAGEMENT = {
  SEARCH: 'search',
  PRODUCT_VIEW: 'product_view',
  NO_RESULTS: 'no_results',
};

const SESSION_STATE_KEY = 'shopsmt-beta-toolbar-session';
const SOFT_DELAY_MS = 45_000;
const MIN_PRODUCT_VIEWS = 2;

/** @type {Set<() => void>} */
const subscribers = new Set();

/**
 * @typedef {{
 *   dismissedAt: number | null;
 *   dismissLocation: string | null;
 *   hasNavigatedAfterDismiss: boolean;
 *   productViewCount: number;
 *   hasSearch: boolean;
 *   hasNoResults: boolean;
 * }} BetaToolbarSessionState
 */

/** @returns {BetaToolbarSessionState} */
function defaultSessionState() {
  return {
    dismissedAt: null,
    dismissLocation: null,
    hasNavigatedAfterDismiss: false,
    productViewCount: 0,
    hasSearch: false,
    hasNoResults: false,
  };
}

/**
 * @param {Storage} storage
 * @param {string} key
 */
function readJson(storage, key) {
  try {
    const raw = storage.getItem(key);
    if (raw == null || raw === '') {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @param {unknown} value
 */
function writeJson(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}

/** @returns {BetaToolbarSessionState} */
function readSessionState() {
  const data = readJson(sessionStorage, SESSION_STATE_KEY);
  if (data == null || typeof data !== 'object') {
    return defaultSessionState();
  }

  return {
    dismissedAt: typeof data.dismissedAt === 'number' ? data.dismissedAt : null,
    dismissLocation:
      typeof data.dismissLocation === 'string' ? data.dismissLocation : null,
    hasNavigatedAfterDismiss: data.hasNavigatedAfterDismiss === true,
    productViewCount:
      typeof data.productViewCount === 'number' ? data.productViewCount : 0,
    hasSearch: data.hasSearch === true,
    hasNoResults: data.hasNoResults === true,
  };
}

/** @param {BetaToolbarSessionState} state */
function writeSessionState(state) {
  writeJson(sessionStorage, SESSION_STATE_KEY, state);
}

function readPersistedDismissedAt() {
  try {
    const raw = localStorage.getItem(SITE_BETA_DISCLAIMER_DISMISSED_AT_KEY);
    if (raw == null || raw === '') {
      return null;
    }
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function notifySubscribers() {
  for (const callback of subscribers) {
    callback();
  }
}

export function isBetaToolbarPersistentlyEligible() {
  try {
    return localStorage.getItem(SITE_BETA_TOOLBAR_ELIGIBLE_KEY) === '1';
  } catch {
    return false;
  }
}

function markPersistentlyEligible() {
  if (isBetaToolbarPersistentlyEligible()) {
    return;
  }

  try {
    localStorage.setItem(SITE_BETA_TOOLBAR_ELIGIBLE_KEY, '1');
  } catch {
    /* ignore */
  }
  notifySubscribers();
}

/** @param {BetaToolbarSessionState} state */
function meetsEngagementCriteria(state) {
  return (
    state.hasSearch
    || state.hasNoResults
    || state.productViewCount >= MIN_PRODUCT_VIEWS
  );
}

/** @param {BetaToolbarSessionState} state */
function meetsSoftDelayCriteria(state) {
  if (state.dismissedAt == null) {
    return false;
  }

  return (
    Date.now() - state.dismissedAt >= SOFT_DELAY_MS
    && state.hasNavigatedAfterDismiss
  );
}

/** @param {BetaToolbarSessionState} state */
function maybeUnlock(state) {
  if (isBetaToolbarPersistentlyEligible()) {
    return;
  }

  if (!meetsEngagementCriteria(state) || !meetsSoftDelayCriteria(state)) {
    notifySubscribers();
    return;
  }

  markPersistentlyEligible();
}

/**
 * @param {boolean} disclaimerDismissed
 */
export function canShowBetaToolbar(disclaimerDismissed) {
  if (!disclaimerDismissed) {
    return false;
  }

  if (isBetaToolbarPersistentlyEligible()) {
    return true;
  }

  const state = readSessionState();
  if (!meetsEngagementCriteria(state) || !meetsSoftDelayCriteria(state)) {
    return false;
  }

  markPersistentlyEligible();
  return true;
}

/**
 * @param {string} locationKey
 */
export function onBetaDisclaimerDismissed(locationKey) {
  const dismissedAt = Date.now();

  try {
    localStorage.setItem(
      SITE_BETA_DISCLAIMER_DISMISSED_AT_KEY,
      String(dismissedAt),
    );
  } catch {
    /* ignore */
  }

  writeSessionState({
    ...readSessionState(),
    dismissedAt,
    dismissLocation: locationKey,
    hasNavigatedAfterDismiss: false,
  });
  notifySubscribers();

  window.setTimeout(() => {
    maybeUnlock(readSessionState());
  }, SOFT_DELAY_MS);
}

/**
 * @param {boolean} disclaimerDismissed
 * @param {string} locationKey
 */
export function ensureBetaToolbarSession(disclaimerDismissed, locationKey) {
  if (!disclaimerDismissed || isBetaToolbarPersistentlyEligible()) {
    return;
  }

  const state = readSessionState();
  if (state.dismissedAt != null && state.dismissLocation != null) {
    return;
  }

  writeSessionState({
    ...defaultSessionState(),
    dismissedAt: readPersistedDismissedAt() ?? Date.now(),
    dismissLocation: locationKey,
  });
  notifySubscribers();
}

/**
 * @param {string} locationKey
 */
export function onBetaToolbarRouteChange(locationKey) {
  const state = readSessionState();
  if (
    state.dismissedAt == null
    || state.dismissLocation == null
    || state.hasNavigatedAfterDismiss
  ) {
    return;
  }

  if (locationKey === state.dismissLocation) {
    return;
  }

  const next = {
    ...state,
    hasNavigatedAfterDismiss: true,
  };
  writeSessionState(next);
  maybeUnlock(next);
}

/**
 * @param {string} action
 */
export function recordBetaEngagement(action) {
  const state = readSessionState();
  const next = { ...state };

  if (action === BETA_ENGAGEMENT.SEARCH) {
    next.hasSearch = true;
  } else if (action === BETA_ENGAGEMENT.PRODUCT_VIEW) {
    next.productViewCount += 1;
  } else if (action === BETA_ENGAGEMENT.NO_RESULTS) {
    next.hasNoResults = true;
  } else {
    return;
  }

  writeSessionState(next);
  maybeUnlock(next);
}

/**
 * @param {() => void} callback
 * @returns {() => void}
 */
export function subscribeBetaToolbarEligibility(callback) {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}
